<template>
  <section class="market-pulse" aria-label="Live market pulse">
    <!-- Loading: 4 grey shimmer pills (reserve exact height to avoid CLS) -->
    <div v-if="loading" class="pulse-grid" aria-hidden="true">
      <div v-for="n in 4" :key="`pulse-skel-${n}`" class="pulse-skeleton shimmer"></div>
    </div>

    <!-- Error: static fallback line instead of blank strip -->
    <p v-else-if="error" class="pulse-fallback">
      Tracking 6,000+ sailings across major cruise lines
    </p>

    <!-- Loaded: 4 stat modules -->
    <div v-else class="pulse-grid">
      <div class="pulse-stat">
        <i class="fas fa-ship pulse-icon" aria-hidden="true"></i>
        <span class="pulse-metric">{{ formatNumber(stats.total_sailings) }}</span>
        <span class="pulse-label">Sailings Tracked</span>
      </div>
      <div class="pulse-stat">
        <i class="fas fa-anchor pulse-icon" aria-hidden="true"></i>
        <span class="pulse-metric">{{ formatNumber(stats.total_lines) }}</span>
        <span class="pulse-label">Major Cruise Lines</span>
      </div>
      <div class="pulse-stat">
        <i class="fas fa-sync-alt pulse-icon" aria-hidden="true"></i>
        <span class="pulse-metric">{{ formatNumber(stats.price_updates_24h) }}</span>
        <span class="pulse-label">Price Checks Today</span>
      </div>
      <div class="pulse-stat">
        <i class="fas fa-shield-alt pulse-icon" aria-hidden="true"></i>
        <span class="pulse-metric">100% Free</span>
        <span class="pulse-label">Independent &amp; Unbiased</span>
      </div>
    </div>
  </section>
</template>

<script>
import apiService from '@/services/api'

export default {
  name: 'MarketPulseStrip',
  data() {
    return {
      loading: true,
      error: null,
      stats: {
        total_sailings: 0,
        total_lines: 0,
        price_updates_24h: 0
      }
    }
  },
  mounted() {
    this.fetchStats()
  },
  methods: {
    async fetchStats() {
      this.loading = true
      this.error = null
      try {
        const response = await apiService.getHomeStats()
        this.stats = response.data || this.stats
      } catch (e) {
        console.error('Error fetching home stats:', e)
        this.error = 'unavailable'
      } finally {
        this.loading = false
      }
    },
    formatNumber(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '0'
      return n.toLocaleString('en-US')
    }
  }
}
</script>

<style scoped>
.market-pulse {
  background: var(--cs-signal-mist);
  border-top: 1px solid var(--color-border-light);
  border-bottom: 1px solid var(--color-border-light);
  padding: var(--space-3) var(--space-4);
}

.pulse-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-3);
  align-items: center;
  max-width: 1100px;
  margin: 0 auto;
}

.pulse-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 2px;
}

.pulse-icon {
  font-size: 1.1rem;
  color: var(--cs-signal-teal);
  margin-bottom: 2px;
}

.pulse-metric {
  font-family: var(--font-heading);
  font-size: var(--text-h3);
  font-weight: 700;
  color: var(--cs-ink);
  line-height: 1.2;
}

.pulse-label {
  font-size: var(--text-small);
  color: var(--cs-muted);
}

.pulse-fallback {
  margin: 0;
  text-align: center;
  color: var(--cs-muted);
  font-size: var(--text-body);
  padding: var(--space-2) 0;
}

/* Skeleton pills reserve exact module height (36px) */
.pulse-skeleton {
  height: 36px;
  border-radius: var(--radius-pill);
  align-self: center;
}

.shimmer {
  background: linear-gradient(
    90deg,
    var(--color-border-light) 0%,
    var(--cs-card) 40%,
    var(--color-border-light) 80%
  );
  background-size: 300% 100%;
  animation: pulse-shimmer 1.4s ease-in-out infinite;
}

@keyframes pulse-shimmer {
  0% { background-position: 150% 0; }
  100% { background-position: -150% 0; }
}

/* Mobile: 2x2 grid with tighter type */
@media (max-width: 768px) {
  .pulse-grid {
    grid-template-columns: repeat(2, 1fr);
    row-gap: var(--space-3);
  }

  .pulse-icon {
    font-size: 16px;
  }

  .pulse-metric {
    font-size: 14px;
  }

  .pulse-label {
    font-size: 11px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .shimmer {
    animation: none;
  }
}
</style>